import { filter, firstValueFrom, Observable, tap, timeout } from 'rxjs';
import { logError } from '@ardoq/logging';
import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
  withNamespace,
} from '@ardoq/rxbeach';
import { nextDOMUpdate } from '@ardoq/common-helpers';
import {
  GetAdditionalRoutineArgs,
  NavigatorConfiguration,
  NavigatorLayoutState,
} from '../../types';
import {
  applyInitialTreeConfiguration,
  expandNodeInTree,
  setFilterTerm,
} from '../../actions/navigatorActions';
import { isDisplayedAndTreeHasComponents } from '../../utils/isDisplayedAndTreeHasComponent';
import { scrollNodeIntoViewIfNeeded } from '../../utils/scrollIntoViewUtils';
import { COMPONENTS_LOAD_TIMEOUT_MS } from '../../consts';

export const getApplyInitialTreeConfigurationRoutine = ({
  navigator$,
  navigatorConfiguration,
}: GetAdditionalRoutineArgs) =>
  action$.pipe(
    withNamespace(navigatorConfiguration.actionNamespace),
    ofType(applyInitialTreeConfiguration),
    extractPayload(),
    tap(payload => {
      applyConfiguration({
        navigator$,
        navigatorConfiguration,
        ...payload,
      });
    })
  );

type ApplyConfigurationArgs = {
  navigator$: Observable<NavigatorLayoutState>;
  navigatorConfiguration: NavigatorConfiguration;
  filterTerm?: string;
  componentId?: string;
};

const applyConfiguration = async ({
  navigator$,
  navigatorConfiguration,
  filterTerm,
  componentId,
}: ApplyConfigurationArgs) => {
  const { actionNamespace } = navigatorConfiguration;
  if (filterTerm !== undefined) {
    dispatchAction(setFilterTerm(filterTerm), actionNamespace);
  }
  if (!componentId) {
    return;
  }
  try {
    const state = await firstValueFrom(
      navigator$.pipe(
        filter(isDisplayedAndTreeHasComponents([componentId])),
        timeout(COMPONENTS_LOAD_TIMEOUT_MS)
      )
    );
    dispatchAction(expandNodeInTree({ nodeId: componentId }), actionNamespace);
    await nextDOMUpdate(state.container);
    const updatedState = await firstValueFrom(navigator$);
    scrollNodeIntoViewIfNeeded(updatedState, componentId);
  } catch {
    logError(
      Error(
        `Initial tree configuration couldn't be applied: component "${componentId}" not available in tree`
      )
    );
  }
};
